import { useEffect, useRef, useState } from 'react';
import { KeyCaps } from './KeyCaps';

/**
 * Help tags, the platform's name for tooltips.
 *
 * One layer for the whole app rather than a wrapper round every button: a control says
 * what it is with `data-tip` (and its shortcut with `data-tip-keys`), and this watches the
 * pointer and draws the tag. So a button anywhere — the toolbar, the side panel, a menu
 * row a tool declared — gets a tooltip by saying one word, and none of them pays for a
 * component of its own.
 *
 * It waits before it appears, as the system's does, and once one is showing the next one
 * along appears at once: sweeping across a toolbar reads every mark without a pause at
 * each. A press hides it — a tooltip under the pointer that just clicked is in the way.
 */

const DELAY = 550;
/** How long after one closes the next opens without waiting. */
const WARM = 400;

interface Tip {
  readonly label: string;
  readonly keys: string | null;
  readonly x: number;
  readonly y: number;
  readonly above: boolean;
}

export function TooltipLayer() {
  const [tip, setTip] = useState<Tip | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warmUntil = useRef(0);
  const current = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const clear = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
    const hide = () => {
      clear();
      if (current.current) warmUntil.current = performance.now() + WARM;
      current.current = null;
      setTip(null);
    };
    const show = (element: HTMLElement) => {
      const label = element.dataset.tip;
      if (!label) return;
      const box = element.getBoundingClientRect();
      // Below the control, unless that would run off the window — then above it.
      const above = box.bottom + 40 > window.innerHeight;
      setTip({
        label,
        keys: element.dataset.tipKeys ?? null,
        x: box.left + box.width / 2,
        y: above ? box.top - 8 : box.bottom + 8,
        above,
      });
    };
    // A tool guide coming up in the same place takes over from the tag (editor/shell/GuidePeek).
    window.addEventListener('artwork:hide-tip', hide);
    const over = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      // Pointer capture can enter a resized handle again; keep its help tag out of
      // the way of live drag feedback until the pointer is released.
      if (event.buttons !== 0) { hide(); return; }
      const element = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-tip]') ?? null;
      if (element === current.current) return;
      clear();
      current.current = element;
      if (!element) {
        if (tip) warmUntil.current = performance.now() + WARM;
        setTip(null);
        return;
      }
      if (performance.now() < warmUntil.current) show(element);
      else {
        setTip(null);
        timer.current = setTimeout(() => {
          if (current.current === element && element.isConnected) show(element);
        }, DELAY);
      }
    };
    document.addEventListener('pointerover', over, true);
    document.addEventListener('pointerdown', hide, true);
    document.addEventListener('keydown', hide, true);
    window.addEventListener('blur', hide);
    window.addEventListener('scroll', hide, true);
    return () => {
      clear();
      window.removeEventListener('artwork:hide-tip', hide);
      document.removeEventListener('pointerover', over, true);
      document.removeEventListener('pointerdown', hide, true);
      document.removeEventListener('keydown', hide, true);
      window.removeEventListener('blur', hide);
      window.removeEventListener('scroll', hide, true);
    };
    // `tip` is read only to decide whether the next one opens warm; stale is fine there.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!tip) return null;
  return (
    <div
      role="tooltip"
      className="ui-fade pointer-events-none fixed z-[100] flex max-w-[260px] items-center gap-2 rounded-[7px] px-2 py-[5px] text-[12px] leading-[15px]"
      style={{
        left: Math.max(8, Math.min(window.innerWidth - 8, tip.x)),
        top: tip.y,
        transform: `translate(-50%, ${tip.above ? '-100%' : '0'})`,
        background: 'var(--tooltip-bg)',
        color: 'var(--tooltip-label)',
        boxShadow: '0 0 0 0.5px var(--glass-border), 0 6px 20px -6px rgb(0 0 0 / 0.45)',
      }}
    >
      <span>{tip.label}</span>
      {tip.keys ? <KeyCaps shortcut={tip.keys} /> : null}
    </div>
  );
}

/** The attributes that give an element a help tag. */
export function tip(label: string, keys?: string): { 'data-tip': string; 'data-tip-keys'?: string } {
  return keys ? { 'data-tip': label, 'data-tip-keys': keys } : { 'data-tip': label };
}
